import React, { useRef, useState, useCallback } from "react";
import { NodeViewWrapper } from "@tiptap/react";

const ALIGN_CLASS = {
  // Left/right float so text wraps beside the image on desktop (and
  // continues below once the text runs taller) — kept in sync with the
  // identical mapping in RichTextEditor.jsx's `AlignedImage` extension
  // so the live editor and the saved/published HTML always match.
  // Mobile drops the float entirely (see index.css) so it always stacks
  // above the text there instead.
  left: "float-left mr-5 mb-2 rounded-md",
  right: "float-right ml-5 mb-2 rounded-md",
  center: "block mx-auto mb-2 rounded-md",
};

const HANDLE_BASE =
  "absolute h-3 w-3 rounded-sm bg-white border-2 border-[#0A0F17] shadow ring-1 ring-black/10";

/**
 * NodeView for an image inside the rich-text editor.
 *
 * - Wraps an <img> with the same align class as before so floats / centring keep working.
 * - When the node is `selected` (TipTap sets this when the user clicks the image),
 *   four corner handles appear and the image gets a brand-accent ring.
 * - Dragging any handle resizes the image by computing a new percent of the
 *   editor's content width. Min 10%, max 100%, snapped to whole percents.
 */
export default function ResizableImageNodeView({
  node,
  selected,
  updateAttributes,
  editor,
}) {
  const wrapperRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [livePercent, setLivePercent] = useState(null);
  const [addingCaption, setAddingCaption] = useState(false);

  const alignment = node.attrs.alignment || "center";
  const widthAttr = node.attrs.width; // null or 1-100 (%)
  const caption = node.attrs.caption || "";
  const linkHref = node.attrs.href || "";
  const containerClass = ALIGN_CLASS[alignment] || ALIGN_CLASS.center;

  // The percent we render at — falls back to the saved attribute, or 100% for floats
  // (because the underlying <img> has no explicit width set yet) but for centred
  // images we let the natural max-w-full kick in.
  const visiblePercent = livePercent ?? widthAttr;

  const startDrag = useCallback(
    (corner) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      const editorEl =
        editor?.view?.dom || wrapperRef.current?.closest(".tiptap-prose");
      const editorRect = editorEl?.getBoundingClientRect();
      const editorWidth = editorRect?.width || 600;
      const wrapperRect = wrapperRef.current?.getBoundingClientRect();
      const startX = e.clientX;
      const startWidth = wrapperRect?.width || editorWidth;
      // Direction: handles on the right grow with +deltaX, on the left grow with -deltaX
      const dir = corner === "tr" || corner === "br" ? 1 : -1;

      setDragging(true);

      // Disable pointer events on all iframes so they do not steal mouse events during dragging
      const iframes = document.querySelectorAll("iframe");
      iframes.forEach((ifr) => ifr.style.pointerEvents = "none");

      const onMove = (ev) => {
        const delta = (ev.clientX - startX) * dir;
        const newPx = Math.max(40, startWidth + delta); // min 40px so the handle stays grabbable
        const pct = Math.min(
          100,
          Math.max(10, Math.round((newPx / editorWidth) * 100)),
        );
        setLivePercent(pct);
      };

      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        // Re-enable pointer events on iframes
        const ifrs = document.querySelectorAll("iframe");
        ifrs.forEach((ifr) => ifr.style.pointerEvents = "");
        
        setDragging(false);
        setLivePercent((finalPct) => {
          if (finalPct != null) updateAttributes({ width: finalPct });
          return null;
        });
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [editor, updateAttributes],
  );

  const inlineStyle = visiblePercent
    ? { width: `${visiblePercent}%`, height: "auto" }
    : undefined;

  return (
    <NodeViewWrapper
      ref={wrapperRef}
      as="div"
      data-alignment={alignment}
      data-width={widthAttr || ""}
      className={`relative ${containerClass}`}
      style={inlineStyle}
      contentEditable={false}
      draggable={false}
    >
      <img
        src={node.attrs.src}
        alt={node.attrs.alt || ""}
        title={node.attrs.title || ""}
        className="block w-full rounded-md"
        draggable={false}
      />

      {/* Link indicator — shows both when the image has a URL set AND
          when it's currently selected, so the admin can see the current
          link target. Click the toolbar "Link" button to edit/remove
          (the toolbar reroutes to the image-href editor when an image
          is selected). */}
      {linkHref ? (
        <span
          data-testid="image-link-badge"
          className="pointer-events-none absolute left-2 top-2 flex max-w-[85%] items-center gap-1 rounded-sm bg-[#0A0F17]/85 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow"
          title={`Links to ${linkHref}`}
        >
          <span aria-hidden>🔗</span>
          <span className="truncate">{linkHref}</span>
        </span>
      ) : null}

      {/* Caption / attribution. No box/border and no reserved space at
          all unless there's actual text — an empty placeholder input
          used to always render (bordered) the moment the image was
          selected, leaving a permanent gap under every image whether or
          not an admin ever intended to caption it. Now: real caption →
          plain (borderless) text, editable in place when selected; no
          caption + not selected → nothing; no caption + selected → a
          tiny "+ Add caption" link, no box, which reveals a borderless
          input only once clicked. */}
      {caption || addingCaption ? (
        selected ? (
          <input
            type="text"
            value={caption}
            onChange={(e) => updateAttributes({ caption: e.target.value })}
            onBlur={() => { if (!caption) setAddingCaption(false); }}
            autoFocus={addingCaption}
            placeholder="Add a caption or photo credit (optional)"
            data-testid="image-caption-input"
            className="block w-full border-0 bg-transparent p-0 text-xs italic text-[var(--brand-body)] outline-none"
            onMouseDown={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className="block text-xs italic leading-snug text-[var(--brand-body)]"
            data-testid="image-caption-display"
          >
            {caption}
          </span>
        )
      ) : selected ? (
        <button
          type="button"
          onClick={() => setAddingCaption(true)}
          onMouseDown={(e) => e.stopPropagation()}
          data-testid="image-add-caption-btn"
          className="inline-flex items-center text-[11px] font-semibold text-brand-accent hover:underline"
        >
          + Add caption
        </button>
      ) : null}

      {/* Live size badge while dragging */}
      {dragging && livePercent != null ? (
        <span
          className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 rounded-sm bg-[#0A0F17] px-2 py-0.5 font-display text-xs font-bold text-white"
          data-testid="image-resize-badge"
        >
          {livePercent}%
        </span>
      ) : null}

      {/* Resize handles — only when selected */}
      {selected ? (
        <>
          <span
            data-testid="image-handle-tl"
            onMouseDown={startDrag("tl")}
            className={`${HANDLE_BASE} -left-1.5 -top-1.5 cursor-nwse-resize`}
          />
          <span
            data-testid="image-handle-tr"
            onMouseDown={startDrag("tr")}
            className={`${HANDLE_BASE} -right-1.5 -top-1.5 cursor-nesw-resize`}
          />
          <span
            data-testid="image-handle-bl"
            onMouseDown={startDrag("bl")}
            className={`${HANDLE_BASE} -bottom-1.5 -left-1.5 cursor-nesw-resize`}
          />
          <span
            data-testid="image-handle-br"
            onMouseDown={startDrag("br")}
            className={`${HANDLE_BASE} -bottom-1.5 -right-1.5 cursor-nwse-resize`}
          />
        </>
      ) : null}
    </NodeViewWrapper>
  );
}
